
import { useEffect } from "react";
import type { Company } from "@/types";

interface CompanyMetadataProps {
  company: Company | {
    id: string;
    name: string;
    logo_url?: string | null;
    owner_id?: string;
    plan?: string;
    slug?: string;
  };
  pageType: "feedback" | "help";
}

const CompanyMetadata = ({ company, pageType }: CompanyMetadataProps) => {
  useEffect(() => {
    // Don't update metadata if company data is missing or invalid
    if (!company || typeof company !== 'object') return;
    
    // Safely extract and validate company properties to ensure they're strings
    const companyName = typeof company.name === 'string' ? company.name : '';
    if (!companyName) return; // Company name is required
    
    const pageTitle = pageType === "feedback" 
      ? `${companyName} - Feedback Portal` 
      : `${companyName} - Help Desk`;

    const pageDescription = pageType === "feedback"
      ? `Share your ideas and feedback with ${companyName}`
      : `Get help and support from ${companyName}`;

    // Update the document title
    document.title = pageTitle;
    
    // Find or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', pageDescription);
    
    // Handle OG and Twitter meta tags
    updateMetaTag('og:title', pageTitle);
    updateMetaTag('og:description', pageDescription);
    updateMetaTag('twitter:title', pageTitle);
    updateMetaTag('twitter:description', pageDescription);
    
    // Handle favicon and image meta tags if logo_url exists
    const logoUrl = company.logo_url && typeof company.logo_url === 'string' 
      ? company.logo_url 
      : null;
      
    if (logoUrl) {
      // Update favicon
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = logoUrl;
      
      // Update OG and Twitter image tags
      updateMetaTag('og:image', logoUrl);
      updateMetaTag('twitter:image', logoUrl);
    }
    
    // Cleanup function to reset metadata when component unmounts
    return () => {
      // We could reset to default values here if needed
    };
  }, [company, pageType]);
  
  // Helper function to update or create meta tags
  const updateMetaTag = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  // Component doesn't render anything visible
  return null;
};

export default CompanyMetadata;
